import { platform } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { useEffect, useRef, useState } from 'react'

import { useDisabled } from './disabled.tsx'
import { Icon } from './Icon.tsx'

import './copyId.css'

/**
 * 식별자 한 개를 **원값 그대로** 클립보드에 넣는 작은 버튼.
 *
 * 🔴 **`navigator.clipboard` 를 여기서 부르지 않는다.** 호스트 의존 기능이라
 *    `@ctcells/core` 의 플랫폼 포트(`platform.clipboard`)를 통한다 — 데스크톱 셸(Tauri)로
 *    갈 때 구현체만 갈아끼우고 화면 코드는 그대로다(CLAUDE.md 「브라우저 API 의존은
 *    플랫폼 계층 뒤에」).
 *
 * 🔴 **조용히 실패하지 않는다.** 세 갈래가 전부 «글자»로 나간다.
 *    - 호스트가 못 한다 → **비활성 + 사유**(숨기지 않는다 · §5-6 과 같은 규약)
 *    - 성공 → `aria-live` 구역에 「복사됨」. 눈으로도 보이고 보조기술도 읽는다
 *    - 실패(권한 거부 등) → 같은 구역에 「복사하지 못했습니다」
 *
 * 🔴 **보이는 것은 앞 8자인데 복사되는 것은 원값 전부다**(`ShortId` 와 짝이다).
 *    잘린 값을 복사하면 서버가 못 찾는다 — 라벨이 「전체 ID 복사」인 이유다.
 */
export interface CopyIdProps {
  /** 복사할 원값. 잘린 값을 넘기지 마라 */
  value: string
  /** 버튼 이름. 기본 「전체 ID 복사」 */
  label?: string | undefined
}

type Result = 'idle' | 'done' | 'failed'

/** 「복사됨」이 남아 있는 시간(ms). 상태 표시일 뿐이라 여기 상수 하나로 끝난다 */
const NOTICE_MS = 2000

export function CopyId({ value, label }: CopyIdProps) {
  const [result, setResult] = useState<Result>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  /*
   * ⚠ 언마운트 뒤에 타이머가 살아 남으면 사라진 컴포넌트에 상태를 쓴다.
   *   목록 행에서 빠르게 옮겨 다니면 실제로 난다.
   */
  useEffect(() => () => clearTimeout(timer.current), [])

  const available = platform.clipboard.isAvailable()
  const name = label ?? t('action.copyId')
  const gate = available
    ? ({ disabled: false } as const)
    : ({ disabled: true, disabledReason: t('reason.clipboardUnavailable') } as const)
  const { isDisabled, ariaProps, reasonNode } = useDisabled(gate.disabled, gate.disabledReason)

  const announce = (next: Result) => {
    setResult(next)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setResult('idle'), NOTICE_MS)
  }

  return (
    <span className="a-copy">
      <button
        aria-label={name}
        className="a-copy__btn"
        onClick={
          isDisabled
            ? undefined
            : () => {
                platform.clipboard.writeText(value).then(
                  () => announce('done'),
                  () => announce('failed'),
                )
              }
        }
        type="button"
        {...ariaProps}
      >
        <Icon name="copy" size="sm" />
        {reasonNode}
      </button>
      {/*
       * 🔴 **구역 자체는 늘 있다.** 알림이 생길 때 요소가 나타나면 보조기술이 못 읽고,
       *    옆 글자가 그만큼 밀린다(작업 줄에서 특히 안 된다 — 높이·자리가 고정이다).
       */}
      <span
        aria-live="polite"
        className={result === 'failed' ? 'a-copy__say a-copy__say--failed' : 'a-copy__say'}
      >
        {result === 'idle' ? '' : t(result === 'done' ? 'state.copied' : 'error.copyFailed')}
      </span>
    </span>
  )
}
